/** @jsxImportSource preact */
import { useId } from "preact/hooks";

import * as m from "../../paraglide/messages.js";
import { CARD_STYLES } from "../form/card-styles";
import { FormButton } from "../form/form-button";
import { FORM_STYLES, formVars } from "../form/form-styles";
import { TextField } from "../form/text-field";
import { AlertIcon, RestartIcon } from "../icons";
import type { WidgetTokens } from "../theme";
import {
  VERIFICATION_CODE_STYLES,
  VerificationCodeField,
} from "../verification-code/verification-code-field";

/**
 * 邮箱验证走到哪一步，卡片按它决定画哪些字段和按钮：
 * - initial：填邮箱，发验证码；
 * - sendFailed：验证邮件没发出，按钮换成重试；
 * - codeSent：验证码已发出，等买家填；
 * - codeWrong：验证码不对，报还剩几次；
 * - codeExpired：验证码过期，主按钮换成重新发送；
 * - cooldown：刚重发过，重发键置灰并倒计时；
 * - locked：次数用完，整张表单禁用。
 */
export type WidgetVerifyStage =
  | "codeExpired"
  | "codeSent"
  | "codeWrong"
  | "cooldown"
  | "initial"
  | "locked"
  | "sendFailed";

export interface VerifyEmailCardProps {
  /** 已填的验证码；没填传空。 */
  code?: string;
  /** 重发倒计时，如 `0:30`；cooldown 阶段用。 */
  cooldown?: string;
  /** 邮箱格式不对；只在 initial 阶段有意义。 */
  emailError?: boolean;
  /** 买家填的下单邮箱。 */
  email: string;
  onCodeChange?: (value: string) => void;
  onContactStore?: () => void;
  onEmailChange?: (value: string) => void;
  onResend?: () => void;
  onSendCode?: () => void;
  onVerify?: () => void;
  stage: WidgetVerifyStage;
  tokens: WidgetTokens;
  /** 验证码还能试几次；codeWrong 阶段用。 */
  triesLeft?: number;
  busy?: boolean;
  notice?: string;
}

export const VERIFY_EMAIL_CARD_STYLES = `
.gn-verify-intro { display:flex; flex-direction:column; gap:4px; min-width:0; }
.gn-verify-intro-text { font-size:13px; line-height:1.4; color:var(--gn-form-quiet); }
.gn-verify-fields { display:flex; flex-direction:column; gap:12px; }
.gn-verify-code { display:flex; flex-direction:column; gap:8px; }
.gn-verify-hint { font-size:12px; line-height:1.4; color:var(--gn-form-quiet); }
.gn-verify-alert { display:flex; align-items:flex-start; gap:6px; font-size:12px; line-height:1.4; font-weight:var(--gn-form-strong); color:var(--gn-form-ink); }
.gn-verify-alert svg { flex:none; margin-top:1px; color:var(--gn-form-danger); }
.gn-verify-mono { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:12px; font-variant-numeric:tabular-nums; }
.gn-verify-actions { display:flex; align-items:center; gap:8px; }
.gn-verify-actions > .gn-btn[data-variant="primary"] { flex:1 1 0; min-width:0; }
`;

function triesMessage(triesLeft: number | undefined): string {
  return triesLeft === 1
    ? m.widget_verify_code_wrong_last()
    : m.widget_verify_code_wrong({ tries: String(triesLeft ?? 0) });
}

/**
 * 邮箱验证卡：买家用下单邮箱核验身份。邮箱、六位验证码和按钮都是挂件里的表单件
 * （TextField、VerificationCodeField、FormButton），这张卡只按阶段摆放它们。
 */
export function VerifyEmailCard({
  code,
  cooldown,
  email,
  emailError = false,
  onCodeChange,
  onContactStore,
  onEmailChange,
  onResend,
  onSendCode,
  onVerify,
  stage,
  tokens: t,
  triesLeft,
  busy = false,
  notice,
}: VerifyEmailCardProps) {
  const codeId = useId();
  const locked = stage === "locked" || busy;
  const hasCode = stage !== "initial" && stage !== "sendFailed";
  const codeProblem =
    stage === "codeWrong"
      ? triesMessage(triesLeft)
      : stage === "codeExpired"
        ? m.widget_verify_code_expired()
        : undefined;
  const canResend = stage === "codeSent" || stage === "codeWrong";
  return (
    <div
      aria-label={m.widget_verify_title()}
      class="gn-card"
      role="group"
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${VERIFICATION_CODE_STYLES}${VERIFY_EMAIL_CARD_STYLES}`}</style>
      <div class="gn-card-head">
        <div class="gn-verify-intro">
          <h3 class="gn-card-title">{m.widget_verify_title()}</h3>
          <div class="gn-verify-intro-text">{m.widget_verify_intro()}</div>
        </div>
      </div>
      {notice && (
        <div role="status" class="gn-verify-hint">
          {notice}
        </div>
      )}
      <div class="gn-verify-fields">
        <TextField
          value={email}
          disabled={locked || hasCode}
          error={
            stage === "initial" && emailError
              ? m.widget_verify_email_invalid()
              : undefined
          }
          label={m.widget_verify_email_label()}
          onValueChange={onEmailChange}
          placeholder={m.widget_verify_email_placeholder()}
          tokens={t}
          type="email"
        />
        {hasCode && (
          <div class="gn-verify-code">
            <VerificationCodeField
              value={code}
              disabled={locked}
              id={codeId}
              invalid={codeProblem !== undefined}
              onValueChange={onCodeChange}
              tokens={t}
            />
            {codeProblem ? (
              <div class="gn-verify-alert" role="alert">
                <AlertIcon size={14} />
                <span>{codeProblem}</span>
              </div>
            ) : (
              !locked && (
                <div class="gn-verify-hint">{m.widget_verify_code_hint()}</div>
              )
            )}
          </div>
        )}
      </div>
      <div class="gn-verify-actions">
        {stage === "initial" || stage === "sendFailed" ? (
          <FormButton disabled={busy} fullWidth onClick={onSendCode} tokens={t}>
            {stage === "sendFailed" && <RestartIcon size={16} />}
            {m.widget_verify_send()}
          </FormButton>
        ) : stage === "codeExpired" ? (
          <FormButton fullWidth onClick={onResend} tokens={t}>
            <RestartIcon size={16} />
            {cooldown ? (
              <>
                {m.widget_verify_resend_in()}
                <span class="gn-verify-mono">{cooldown}</span>
              </>
            ) : (
              m.widget_verify_resend()
            )}
          </FormButton>
        ) : stage === "cooldown" ? (
          <FormButton disabled fullWidth tokens={t}>
            {m.widget_verify_resend_in()}
            {cooldown && <span class="gn-verify-mono">{cooldown}</span>}
          </FormButton>
        ) : (
          <FormButton
            disabled={locked}
            fullWidth={!canResend}
            onClick={onVerify}
            tokens={t}
          >
            {m.widget_verify_confirm()}
          </FormButton>
        )}
        {canResend && (
          <FormButton
            disabled={busy || !!cooldown}
            onClick={onResend}
            tokens={t}
            variant="ghost"
          >
            <RestartIcon size={15} strokeWidth={2.2} />
            {cooldown ? (
              <>
                {m.widget_verify_resend_in()}
                <span class="gn-verify-mono">{cooldown}</span>
              </>
            ) : (
              m.widget_verify_resend()
            )}
          </FormButton>
        )}
      </div>
      <FormButton fullWidth onClick={onContactStore} tokens={t} variant="ghost">
        {m.widget_verify_contact_store()}
      </FormButton>
    </div>
  );
}
